import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  FileText,
  Grid3x3,
  Lock,
  Sparkles,
  Trash2,
  AlertCircle,
  PieChart,
  Download,
  Share,
  Smartphone,
} from "lucide-react";
import { useEstimate } from "@/context/EstimateContext";
import { usePro } from "@/context/ProContext";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { exportPdf, exportCsv } from "@/lib/export";
import { formatUSD, formatNum } from "@/lib/format";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TownRibbon } from "@/components/TownRibbon";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const RATE_PRESETS = [
  { label: "Default", value: 0.01, hint: "$0.01/coin" },
  { label: "Standard", value: 0.2, hint: "$20 / 100" },
  { label: "Bulk 20% More", value: 0.167, hint: "$500 / 3,000" },
];

function groupByCategory(items, rate) {
  const map = new Map();
  let total = 0;
  for (const it of items) {
    const cost = it.coins * rate;
    total += cost;
    const g = map.get(it.category);
    if (g) {
      g.coins += it.coins;
      g.cost += cost;
    } else {
      map.set(it.category, { category: it.category, color: it.color, coins: it.coins, cost, pct: 0 });
    }
  }
  const groups = Array.from(map.values());
  for (const g of groups) g.pct = total > 0 ? (g.cost / total) * 100 : 0;
  return groups.sort((a, b) => b.cost - a.cost);
}

export default function Settings() {
  const navigate = useNavigate();
  const {
    projects,
    purchases,
    rate,
    coinsPurchased,
    totalSpent,
    coinsRemaining,
    blendedRate,
    totalCoinsUsed,
    totalProjectCost,
    setRate,
    clearProjects,
    clearPurchases,
  } = useEstimate();
  const { isPro, paid, codeUnlock, clearLocalUnlock } = usePro();
  const { canInstall, installed, isIOSSafari, isIOS, promptInstall } = useInstallPrompt();

  const [rateText, setRateText] = useState(String(rate));
  const [confirmProjects, setConfirmProjects] = useState(false);
  const [confirmPurchases, setConfirmPurchases] = useState(false);

  const hasPurchases = purchases.length > 0;
  const allItems = useMemo(() => projects.flatMap((p) => p.items), [projects]);
  const groups = useMemo(() => groupByCategory(allItems, blendedRate), [allItems, blendedRate]);

  const commitRate = (text) => {
    const cleaned = text.replace(/[^0-9.]/g, "");
    setRateText(cleaned);
    const parsed = parseFloat(cleaned);
    if (Number.isFinite(parsed) && parsed >= 0) setRate(parsed);
  };

  const exportData = () => ({
    projects,
    purchases,
    blendedRate,
    coinsPurchased,
    totalSpent,
    totalCoinsUsed,
    totalProjectCost,
    coinsRemaining,
  });

  const doExport = (kind) => {
    if (!isPro) return navigate("/paywall");
    if (projects.length === 0) return toast.error("Nothing to export", { description: "Add a project with tasks first." });
    try {
      if (kind === "pdf") exportPdf(exportData());
      else exportCsv(exportData());
    } catch {
      toast.error("Export failed", { description: "Could not generate the file. Please try again." });
    }
  };

  const clearAllProjects = () => {
    if (!confirmProjects) return setConfirmProjects(true);
    clearProjects();
    setConfirmProjects(false);
    toast.success("All projects cleared");
  };
  const clearBuys = () => {
    if (!confirmPurchases) return setConfirmPurchases(true);
    clearPurchases();
    setConfirmPurchases(false);
    toast.success("Purchases cleared");
  };

  return (
    <div className="mx-auto max-w-2xl pb-16" data-testid="settings-screen">
      <header className="flex items-center justify-between border-b border-border bg-surface-2 px-4 py-3">
        <button
          data-testid="settings-back-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-1 font-display font-semibold text-brand"
        >
          <ChevronLeft size={22} /> Done
        </button>
        <h1 className="font-display font-bold">Rate &amp; Summary</h1>
        <ThemeToggle />
      </header>

      <div className="px-4 py-5 sm:px-6">
        {hasPurchases && (
          <>
            <SectionLabel>CREDIT SUMMARY</SectionLabel>
            <div className="rounded-xl bg-brand-dark p-4 text-white" data-testid="blended-rate-card">
              <p className="text-sm text-white/70">Blended rate</p>
              <p className="mt-1 font-mono text-2xl font-bold tabular">
                ${blendedRate.toFixed(4)}
                <span className="text-base font-normal text-white/70"> / coin</span>
              </p>
              <p className="mt-1 font-mono text-sm text-white/70 tabular">
                {formatNum(coinsPurchased)} coins purchased for {formatUSD(totalSpent)}
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <StatCard value={formatNum(coinsRemaining)} label="Coins Remaining" />
              <StatCard value={formatUSD(totalSpent)} label="Total Spent" brand />
            </div>
          </>
        )}

        <SectionLabel className="mt-8">
          {hasPurchases ? "FALLBACK RATE (USD)" : "COST PER COIN (USD)"}
        </SectionLabel>
        <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-2 px-4 py-3">
          <span className="font-mono text-2xl font-bold text-brand">$</span>
          <Input
            data-testid="rate-input"
            value={rateText}
            onChange={(e) => commitRate(e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
            className="flex-1 border-0 bg-transparent p-0 font-mono text-2xl font-bold focus-visible:ring-0 tabular"
          />
          <span className="text-sm text-muted-foreground">/ coin</span>
        </div>
        <p className="mt-2 text-sm leading-snug text-muted-foreground">
          {hasPurchases
            ? "Used only if you remove all purchases. Your live rate comes from logged purchases above."
            : "Set the dollar value of a single coin, or log purchases on the home screen for an exact blended rate."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {RATE_PRESETS.map((p) => {
            const active = !hasPurchases && Math.abs(rate - p.value) < 0.0005;
            return (
              <button
                key={p.label}
                data-testid={`rate-preset-${p.label}`}
                onClick={() => {
                  setRateText(String(p.value));
                  setRate(p.value);
                }}
                className={`flex-1 rounded-xl border-[1.5px] px-3 py-2.5 text-left transition-colors ${
                  active ? "border-brand bg-brand-tint" : "border-border bg-surface-2"
                }`}
              >
                <p className="font-display text-sm font-bold">{p.label}</p>
                <p className="font-mono text-xs text-muted-foreground tabular">{p.hint}</p>
              </button>
            );
          })}
        </div>

        <SectionLabel className="mt-8">ALL PROJECTS</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <StatCard value={formatNum(totalCoinsUsed)} label="Coins Used" testId="summary-total-coins" />
          <StatCard value={formatUSD(totalProjectCost)} label="Total Cost" brand testId="summary-total-cost" />
        </div>

        <SectionLabel className="mt-8">COST BY CATEGORY</SectionLabel>
        {groups.length === 0 ? (
          <div
            className="flex flex-col items-center gap-2 rounded-xl bg-surface-2 py-8 text-muted-foreground"
            data-testid="summary-empty"
          >
            <PieChart size={28} />
            <p className="text-sm">Add project tasks to see the distribution.</p>
          </div>
        ) : (
          <div className="rounded-xl bg-surface-2 px-4">
            {groups.map((g, idx) => (
              <div
                key={g.category}
                data-testid={`breakdown-row-${g.category}`}
                className={`py-3 ${idx < groups.length - 1 ? "border-b border-border" : ""}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: g.color }} />
                    <span className="text-sm font-medium">{g.category}</span>
                  </div>
                  <span className="font-mono text-sm font-bold tabular">{formatUSD(g.cost)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-3">
                  <div className="h-full rounded-full" style={{ width: `${g.pct}%`, backgroundColor: g.color }} />
                </div>
                <p className="mt-1 font-mono text-xs text-muted-foreground tabular">
                  {formatNum(g.coins)} coins · {g.pct.toFixed(0)}%
                </p>
              </div>
            ))}
          </div>
        )}

        {!installed && (
          <>
            <SectionLabel className="mt-8">INSTALL APP</SectionLabel>
            <div className="rounded-xl border border-border bg-surface-2 p-4" data-testid="install-card">
              <div className="mb-3 flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-tint">
                  <Smartphone size={20} className="text-brand" />
                </div>
                <p className="text-sm leading-snug text-foreground">
                  Add CreditCost to your home screen for a full-screen, app-like experience — works offline.
                </p>
              </div>
              {canInstall ? (
                <button
                  data-testid="install-app-button"
                  onClick={promptInstall}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 font-display font-bold text-white transition-colors hover:bg-brand-dark"
                >
                  <Download size={18} /> Install app
                </button>
              ) : isIOS || isIOSSafari ? (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="install-ios-hint">
                  On iPhone/iPad: tap the <Share size={15} className="inline text-brand" /> Share icon in Safari,
                  then choose <span className="font-semibold text-foreground">“Add to Home Screen.”</span>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="install-generic-hint">
                  Open your browser menu (⋮) and choose{" "}
                  <span className="font-semibold text-foreground">“Install app”</span> or{" "}
                  <span className="font-semibold text-foreground">“Add to Home screen.”</span>
                </p>
              )}
            </div>
          </>
        )}

        <SectionLabel className="mt-8">EXPORT QUOTE</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <ExportBtn testId="export-pdf-button" onClick={() => doExport("pdf")} locked={!isPro} icon={FileText} label="PDF" />
          <ExportBtn testId="export-csv-button" onClick={() => doExport("csv")} locked={!isPro} icon={Grid3x3} label="CSV" />
        </div>
        {!isPro && (
          <p className="mt-2 text-sm text-muted-foreground">Export is a Pro feature. Tap to unlock PDF &amp; CSV quotes.</p>
        )}

        <SectionLabel className="mt-8">PRO</SectionLabel>
        {isPro ? (
          <div className="rounded-xl bg-brand-tint p-4" data-testid="pro-active-card">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-brand" />
              <p className="font-display font-bold text-brand">
                {paid ? "CreditCost Pro is active" : "Pro trial active"}
              </p>
            </div>
            <p className="mt-1 text-sm leading-snug text-brand/90">
              {paid
                ? "Unlimited projects, exports, and no promo footer are unlocked on this device."
                : "You have full Pro access during your 7-day trial."}
            </p>
            {codeUnlock && (
              <button
                data-testid="deactivate-local-pro"
                onClick={clearLocalUnlock}
                className="mt-3 text-sm font-semibold text-danger"
              >
                Deactivate Pro on this device
              </button>
            )}
          </div>
        ) : (
          <button
            data-testid="upgrade-pro-button"
            onClick={() => navigate("/paywall")}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-4 font-display font-bold text-white transition-colors hover:bg-brand-dark"
          >
            <Sparkles size={18} /> Upgrade to Pro
          </button>
        )}

        {hasPurchases && (
          <ClearBtn testId="clear-purchases-button" confirm={confirmPurchases} onClick={clearBuys}>
            {confirmPurchases ? "Tap again to clear purchases" : "Clear all purchases"}
          </ClearBtn>
        )}
        {projects.length > 0 && (
          <ClearBtn testId="clear-all-button" confirm={confirmProjects} onClick={clearAllProjects}>
            {confirmProjects ? "Tap again to clear ALL projects" : "Clear all projects"}
          </ClearBtn>
        )}

        <TownRibbon />
      </div>
    </div>
  );
}

function SectionLabel({ children, className = "" }) {
  return (
    <p className={`mb-3 font-display text-xs font-bold tracking-wider text-muted-foreground ${className}`}>
      {children}
    </p>
  );
}

function StatCard({ value, label, brand, testId }) {
  return (
    <div className="rounded-xl bg-surface-2 p-4">
      <p className={`font-mono text-xl font-bold tabular ${brand ? "text-brand" : ""}`} data-testid={testId}>
        {value}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function ExportBtn({ testId, onClick, locked, icon: Icon, label }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex items-center justify-center gap-2 rounded-xl border-[1.5px] border-brand bg-surface-2 py-4 font-display font-bold text-brand transition-colors hover:bg-brand-tint"
    >
      {locked ? <Lock size={18} /> : <Icon size={18} />} {label}
    </button>
  );
}

function ClearBtn({ testId, confirm, onClick, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-danger py-3.5 font-display font-semibold text-danger transition-colors ${
        confirm ? "bg-danger/10" : ""
      }`}
    >
      {confirm ? <AlertCircle size={18} /> : <Trash2 size={18} />} {children}
    </button>
  );
}
